/**
 * `hour` — a time of day with no date. monday's own picker did not open under three patient
 * clicks (`editors/hour/tier2-capture.json`), and the README calls it *"A time picker (hour +
 * minute selects)"*, so that is what this is: two selects in the standard popover frame.
 *
 * The cell renders monday's own 12-hour text (`02:48 AM`), which the shared folder owns.
 */
import { getColumnType, hourText, type HourSettings, type HourValue } from '@elizmonday/shared';
import { CellPopover, useEditorDraft } from '../primitives';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { HourSettingsDialog } from './SettingsDialog';

const shared = getColumnType('hour');

export const HOUR_PICKER_WIDTH = 180;

export const hourColumnUi = defineColumnUi<HourValue, HourSettings>({
  type: 'hour',
  icon: 'columnCreationLog',
  gallery: { group: 'dates', keywords: ['hour', 'time', 'clock'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'center',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,

  Cell: ({ value, settings }) => {
    const text = shared.toText(value, settings) ?? '';
    if (text === '') return <span className="ez-cell__empty" aria-hidden="true" />;
    return (
      <span className="ez-cell__text" data-align="center">
        {text}
      </span>
    );
  },

  /**
   * **P-282.** The two selects are one draft that follows the store while untouched — and the
   * dismiss no longer commits it unconditionally, which is what wrote **09:00** (the seed for an
   * empty cell) onto every `hour` cell that was merely opened and clicked away from.
   */
  Editor: ({ value, onCommit, onCancel }) => {
    const editor = useEditorDraft({ hour: value?.hour ?? 9, minute: value?.minute ?? 0 });
    const { hour, minute } = editor.draft;
    const setHour = (next: number): void => editor.set({ hour: next, minute });
    const setMinute = (next: number): void => editor.set({ hour, minute: next });
    return (
      <CellPopover
        label="Pick a time"
        width={HOUR_PICKER_WIDTH}
        onDismiss={() => editor.dismiss(() => onCommit({ hour, minute }), onCancel)}
        onCancel={onCancel}
      >
        <div className="ez-time-picker">
          <select
            className="ez-time-picker__select"
            aria-label="Hour"
            data-testid="hour-select"
            value={hour}
            onChange={(event) => setHour(Number(event.target.value))}
          >
            {Array.from({ length: 24 }, (_, index) => (
              <option key={index} value={index}>
                {hourText({ hour: index, minute: 0 }).slice(0, 2)}
                {index < 12 ? ' AM' : ' PM'}
              </option>
            ))}
          </select>
          <span className="ez-time-picker__colon">:</span>
          <select
            className="ez-time-picker__select"
            aria-label="Minute"
            data-testid="minute-select"
            value={minute}
            onChange={(event) => setMinute(Number(event.target.value))}
          >
            {Array.from({ length: 60 }, (_, index) => (
              <option key={index} value={index}>
                {String(index).padStart(2, '0')}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="ez-picker__row ez-picker__row--centered"
            data-testid="hour-clear"
            onClick={() => onCommit(null)}
          >
            Clear
          </button>
        </div>
      </CellPopover>
    );
  },

  /** The inverse of `"02:48 AM"`. */
  SettingsDialog: HourSettingsDialog,

  fromClipboard(text, settings) {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    const match = /^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i.exec(trimmed);
    if (match === null) throw new Error(`"${trimmed}" is not a time`);
    let hour = Number(match[1]);
    const meridiem = match[3]?.toUpperCase();
    if (meridiem === 'PM' && hour < 12) hour += 12;
    if (meridiem === 'AM' && hour === 12) hour = 0;
    return shared.parseValue({ hour, minute: Number(match[2]) }, settings) as HourValue | null;
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('hour'),
});
